import { makeStyles } from "@mui/styles";
import { AutocompleteMenuPortalContext } from "components/Form/components/Autocomplete/AutocompleteMenuPortalContext";
import { RelationshipRecordForm } from "components/Form/RelationshipRecordForm";
import { centeredPopoverRootStyle, Popover } from "components/Popover/Popover";
import { FeRecordType, FeRelationshipType } from "config/config";
import "./Prompt.scss";
import "./RecordPrompt.scss";

export interface RecordPromptData {
    resourceName: FeRelationshipType;
    recordId: string;
    isReadOnly: boolean;
    isDisabled: boolean;
    parentResourceType?: FeRecordType;
    parentResourceId?: string;
}

export interface RecordPromptProps {
    data: RecordPromptData;
    isOpen: boolean;
    onConfirm?: () => void;
    onCancel?: () => void;
    rootStyle?: any;
    isScrollable?: boolean;
}

export const RecordPrompt = ({
    data,
    isOpen,
    rootStyle = centeredPopoverRootStyle,
    onConfirm,
    onCancel,
    isScrollable,
}: RecordPromptProps) => {
    const paperStyle = {
        width: "600px",
        minWidth: "300px",
        // Bound the width so the dialog is not wider than needed and never forces
        // a horizontal scrollbar on smaller viewports.
        maxWidth: "calc(100vw - 64px)",
        overflow: isScrollable ? "scroll" : "visible",
    };

    const useStyles = makeStyles(() => ({
        rootStyle,
        paperStyle,
    }));
    const classes = useStyles();

    return (
        <Popover
            isOpen={isOpen}
            anchorReference="none"
            classes={{ root: classes.rootStyle, paper: classes.paperStyle }}
            rootClassName="recordPrompt__popover"
            contentClassName="recordPrompt__content"
        >
            <AutocompleteMenuPortalContext.Provider value={true}>
                <RelationshipRecordForm
                    data={data}
                    onCancel={onCancel}
                    onConfirm={onConfirm}
                    isOpen={isOpen}
                    // Keep the user's unsaved edits when a background /ui push
                    // reinitializes the form's initialValues (the store rebuilds
                    // record objects with fresh identities on every push, so any
                    // edit-related-record popover would otherwise silently lose
                    // in-progress edits). role_permission is the documented
                    // exception: it relies on keepDirtyOnReinitialize=false so
                    // that fetched values overwrite the schema-seeded defaults
                    // (see RelationshipRecordForm). Confine that behaviour to
                    // role_permission; every other record type keeps its edits.
                    keepDirtyOnReinitialize={data.resourceName !== "role_permission"}
                />
            </AutocompleteMenuPortalContext.Provider>
        </Popover>
    );
};
